// Copyright (c) Docugami, Inc. All rights reserved.

import { IconButton } from "@fluentui/react";
import { dgFontFamily } from "components/common/Fonts";
import { IDocumentCommand } from "components/file_management/DocumentCommand";
import { appContentColor, homeGray } from "components/shell/presentation/AppStyles";
import { SourcePicker } from "components/storage/SourcePicker";
import { CoachmarkInfoText } from "components/work_path/InfoView";
import { dgIconNames, useDgIcons } from "hooks/dgIcons";
import { ProviderId } from "networking/StorageProviders";
import { useMemo } from "react";
import { getIconForStorageProvider, getProviderFriendlyName } from "storage/StorageProviderHelpers";
import styled from "styled-components";
import { AutomationId } from "utils/Automation";

const OverlayContainer = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    background-color: rgba(255, 255, 255, 0.85);
    gap: 10px;
`;

const OverlayCenteredContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
`;

export const AttentionText = styled.span`
    margin-top: auto;
    font-family: ${dgFontFamily};
    font-size: 14px;
    font-weight: 300;
    color: ${appContentColor};
`;

const DocPickerContainer = styled.div`
    width: 100%;
    justify-content: space-between;
`;

const OverlayText = styled(CoachmarkInfoText)`
    font-size: 24px;
    width: 100%;
    text-align: center;
`;

const defaultIconDimension = 100;

interface AddDocsPickerProps {
    hidden?: boolean;
    selectedId: string | null;
    setSelectedId: (id: string | null) => void;
    localDocsUploadCommands: IDocumentCommand[];
    overlays: JSX.Element[];
    onClose?: () => void;
    iconDimension?: number;
    className?: string;
}

export const AddDocsPicker = (props: AddDocsPickerProps): JSX.Element => {
    const dgIcons = useDgIcons();

    const iconDimension = useMemo(()=>{
        return props.iconDimension || defaultIconDimension;
    },[props.iconDimension]);

    const localDocsIcon = useMemo(() =>
        dgIcons.icon(
            dgIconNames.DgComputerDocs,
            '',
            iconDimension,
            iconDimension,
            '',
            ''
        ), [dgIcons, iconDimension]);

    const localDocsItem = useMemo(() => {
        const id = 'localdocs';
        return {
            id: id,
            icon: localDocsIcon,
            name: 'My Computer',
            isSelected: props.selectedId === id,
            onSelected: () => props.setSelectedId(id),
            commands: props.localDocsUploadCommands.map(c => {
                return {
                    key: c.id,
                    name: c.label,
                    onClick: c.execute,
                    disabled: !c.isEnabled
                }
            })
        }
    }, [localDocsIcon, props]);

    const providerItems = useMemo(() => {
        return Object.values(ProviderId).map(id => {
            return {
                id: id,
                icon: getIconForStorageProvider(id, iconDimension, iconDimension, iconDimension, iconDimension),
                name: getProviderFriendlyName(id),
                isSelected: props.selectedId === id,
                onSelected: () => props.setSelectedId(id)
            }
        })
    }, [iconDimension, props]);

    const allItems = useMemo(() => [localDocsItem, ...providerItems], [providerItems, localDocsItem]);

    return (
        <>
            <DocPickerContainer
                data-automation-id={AutomationId.DocExplorerSourceOptions}
                className={props.className}
            >
                <SourcePicker hidden={props.hidden} items={allItems} />
            </DocPickerContainer>
            {props.overlays}
        </>
    );
}


export const AddDocsPickerOverlay = (props: AddDocsPickerProps): JSX.Element => (
    <OverlayContainer>
        {!!props.onClose && <IconButton onClick={props.onClose} iconProps={{ iconName: "ChromeClose" }} styles={{ root: { marginLeft: 'auto' }, icon: { color: homeGray } }} />}
        <OverlayCenteredContainer>
            <OverlayText>Choose a source below:</OverlayText>
            <AddDocsPicker {...props} />
        </OverlayCenteredContainer>
    </OverlayContainer>
);
